import Cookies from "js-cookie";

// Resume positions for audio/video classes.
//
// These used to be stored as cookies keyed by the full media URL. Media URLs
// run ~265 characters, so every class a listener opened added a ~275 byte
// cookie that was then sent on every single request. At roughly 43 saved
// classes the request headers outgrew nginx's buffer; over HTTP/2 nginx
// answered by resetting the connection, which Cloudflare reported to the
// listener as a 520. Heavy listeners locked themselves out of the site.
//
// localStorage is the right home for this: it is never sent to the server and
// allows ~5MB instead of ~4KB.
export const STORAGE_PREFIX = "playbackPosition:";

const storageKey = (mediaUrl) => `${STORAGE_PREFIX}${mediaUrl}`;

// Private browsing and storage-disabled setups can throw on access. A lost
// resume position must never break playback.
const withStorage = (fn, fallback = null) => {
  try {
    return fn();
  } catch (error) {
    console.warn("Playback position storage unavailable:", error);
    return fallback;
  }
};

export const savePosition = (mediaUrl, currentTime) =>
  withStorage(() => {
    if (!mediaUrl) return null;
    localStorage.setItem(storageKey(mediaUrl), String(currentTime));
    return null;
  });

export const getPosition = (mediaUrl) =>
  withStorage(() => localStorage.getItem(storageKey(mediaUrl)) ?? null);

export const clearPosition = (mediaUrl) =>
  withStorage(() => {
    localStorage.removeItem(storageKey(mediaUrl));
    return null;
  });

// handleHtmlAttributes injects ontimeupdate="setCookie('<src>', '<id>')" into
// markup that comes back from the API, so a global named `setCookie` has to
// exist on the page. The name is kept for compatibility with that generated
// markup; it writes to localStorage now.
export const inlinePlayerScript = () => `
        function setCookie(src, id) {
          var media = document.getElementById(id);
          // The listener can leave the page mid-update; bail rather than throw.
          if (!media) {
            console.warn("Media element not found for src:", src);
            return;
          }
          try {
            localStorage.setItem(${JSON.stringify(STORAGE_PREFIX)} + src, String(media.currentTime));
          } catch (error) {
            console.warn("Playback position storage unavailable:", error);
          }
        }
      `;

// One-time cleanup for listeners already carrying the oversized cookies.
// Without this they stay locked out until they clear cookies by hand, and they
// would lose every saved position. Returns how many cookies were migrated.
export const migrateLegacyCookies = () =>
  withStorage(() => {
    const all = Cookies.get() || {};
    let migrated = 0;

    Object.entries(all).forEach(([name, value]) => {
      // Position cookies are keyed by media URL; nothing else on the domain is.
      if (!/^https?:\/\//.test(name)) return;

      // A position already in localStorage is newer than the cookie, so keep it
      // and just drop the cookie.
      if (localStorage.getItem(storageKey(name)) === null) {
        localStorage.setItem(storageKey(name), value);
      }
      Cookies.remove(name);
      migrated += 1;
    });

    return migrated;
  }, 0);
